import React from 'react';

const PersonalAction = ({ scoreAllList, userName, createdAt }) => {
  const getStatus = (name, fieldName) => {
    const item = scoreAllList?.find((ele) => ele.name === name);
    return item ? item[fieldName] : "";
  };

  const ritualsPhysical = [
    {
      id: 'R1',
      text: '90 minutes before bed - begin a wind-down ritual (lights down, screens off, no email) to protect sleep quality.',
    },
    {
      id: 'R2',
      text: 'Every 90-120 minutes at the desk - stand, walk, or stretch for 2-3 minutes to oscillate out of seated stress.',
    },
    {
      id: 'R3',
      text: 'Mid-morning and mid-afternoon - eat a small protein-based snack to keep blood sugar and energy stable.',
    },
    {
      id: 'R4',
      text: 'Same time, 3-4 mornings per week - complete a scheduled 30-minute workout before the day\'s demands.',
    },
    {
      id: 'R5',
      text: 'On waking and with each meal - drink a full glass of water before reaching for caffeine.',
    },
  ];

  const ritualsEmotions = [
    {
      id: 'R1',
      text: 'Each morning - name three things you are grateful for to prime a positive emotional state.',
    },
    {
      id: 'R2',
      text: 'Before a high-pressure meeting - take three slow breaths and reframe the moment as challenge, not threat.',
    },
    {
      id: 'R3',
      text: 'When you feel frustration rising - pause, label the emotion, and consciously shift to a constructive response.',
    },
    {
      id: 'R4',
      text: 'End of workday - a shutdown ritual (close the laptop, a short walk) that signals emotional disconnection.',
    },
    {
      id: 'R5',
      text: 'Weekly - schedule one renewing activity or relationship that is purely for enjoyment, not productivity.',
    },
  ];

  const ritualsMental = [
    {
      id: 'R1',
      text: 'First 90 minutes of the day - a single deep-work block on your top priority, notifications off, before email.',
    },
    {
      id: 'R2',
      text: 'Start of each work block - silence the phone and close all tabs unrelated to the task.',
    },
    {
      id: 'R3',
      text: 'Twice daily - batch email and messages into fixed windows rather than reacting continuously.',
    },
    {
      id: 'R4',
      text: 'Between intense tasks - take a 5-minute attention reset (no screen) before starting the next.',
    },
    {
      id: 'R5',
      text: 'End of day - write tomorrows top three priorities so your mind can fully disengage overnight.',
    },
  ];

  const ritualsSpiritual = [
    {
      id: 'R1',
      text: 'Sunday evening - review the week ahead and align your calendar with your top priorities and values.',
    },
    {
      id: 'R2',
      text: 'Each morning - restate your single most important why for the day in one sentence.',
    },
    {
      id: 'R3',
      text: 'Before saying yes to a new commitment - check it against your core values and purpose first.',
    },
    {
      id: 'R4',
      text: 'Daily, 5 minutes - journal or reflect on whether your energy matched who you want to be.',
    },
    {
      id: 'R5',
      text: 'Monthly - audit where your time and energy actually went versus what you say matters most.',
    },
  ];

  const dimensions = [
    {
      dimension: "Physical",
      max: 50,
      accentColor: "border-[#006494]",
      headerBg: "bg-[#006494]/5",
      scoreColor: "text-[#006494]",
      iconHexColor: "#006494",
      rituals: ritualsPhysical,
      leveragePoint: "Protect sleep, fuel consistently, and build movement-based recovery into the workday."
    },
    {
      dimension: "Emotional",
      max: 50,
      accentColor: "border-[#A84B2F]",
      headerBg: "bg-[#A84B2F]/5",
      scoreColor: "text-[#A84B2F]",
      iconHexColor: "#A84B2F",
      rituals: ritualsEmotions,
      leveragePoint: "Deliberately access renewing emotions and build genuine disconnection into your day."

    },
    {
      dimension: "Mental",
      max: 50,
      accentColor: "border-[#20808D]",
      headerBg: "bg-[#20808D]/5",
      scoreColor: "text-[#20808D]",
      iconHexColor: "#20808D",
      rituals: ritualsMental,
      leveragePoint: "Defend deep focus from digital interruption and recover attention between intense tasks."

    },
    {
      dimension: "Spiritual",
      max: 50,
      accentColor: "border-[#7A39BB]",
      headerBg: "bg-[#7A39BB]/5",
      scoreColor: "text-[#7A39BB]",
      iconHexColor: "#7A39BB",
      rituals: ritualsSpiritual,
      leveragePoint: "Anchor daily action to purpose and values so energy is invested where it matters most."

    },
  ];

  const reports = dimensions
    .map(item => ({
      ...item,
      score: getStatus(item.dimension, "score") || 0,
      status: getStatus(item.dimension, "status"),
      statusClass: getStatus(item.dimension, "colorClass"),
    }))
    .sort((b, a) => a.score - b.score); // weakest first


  return (
    <>
      {reports.map((report, pageIndex) => (
        <div key={pageIndex} className="p-10 px-6 space-y-6 bg-white">
          <div className="px-12 space-y-6 page-break">
            <div className="flex items-center justify-between w-full pt-4 pb-2 border-b text-[#212121]/85 italic">
              <h1 className="text-lg font-semibold">Energy Profile Report</h1>
              <p className="text-lg font-semibold">{userName} | {createdAt}</p>
            </div>

            {/* Title Block */}
            <div className="mt-6 mb-6">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                {"Personal Action Framework " + (pageIndex > 0 ? "(Continued)" : "")}
              </h1>

              <h2 className="text-base font-medium text-gray-700 mt-2">
                Trigger-driven Positive Energy Rituals - recovery as a strategic discipline
              </h2>

              <p className="text-sm text-gray-600 mt-3 leading-relaxed">
                Rituals beat willpower. Each ritual below is tied to a specific trigger
                (a time, event, or state) so it runs on autopilot. Start with two
                rituals in your weakest dimension; add more only once they are
                automatic. Track completion in the right-hand column.
              </p>
            </div>

            {/* Dimension Summary Banner */}
            <div className={`${report.headerBg} border border-gray-100 border-l-4 ${report.accentColor} p-5 rounded-r-md mb-10`}>
              <div className="flex justify-between items-start">
                <div>
                  <span
                    className={`block text-sm uppercase font-bold ${report.scoreColor} tracking-wider mb-0.5`}
                  >
                    Dimension
                  </span>

                  <span className="text-2xl font-bold text-gray-800">
                    {report.dimension}
                  </span>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-xl font-bold text-gray-800">
                    <span className={report.scoreColor}>{report.score}</span>

                    <span className="text-gray-400 font-bold text-sm">
                      /{report.max}
                    </span>
                  </span>

                  <span
                    className={`text-xs font-bold px-3 py-1 rounded border tracking-wider uppercase ${report.statusClass}`}
                  >
                    {report.status}
                  </span>
                </div>
              </div>

              {/* Leverage Point */}
              <div className="mt-5 pt-4">
                <h4 className="text-base font-bold text-gray-800 uppercase tracking-wide mb-1">
                  Leverage Point
                </h4>

                <p className="text-sm text-gray-700">{report.leveragePoint}</p>
              </div>
            </div>

            {/* Ritual Cards */}
            <div className="space-y-3 pt-4">
              <div className="flex items-center text-lg font-bold text-gray-800 px-2 pb-2">
                <div className="w-16 text-left">Ritual</div>
                <div className="flex-1 pl-4">
                  Positive Energy Ritual (trigger - behaviour)
                </div>
              </div>

              {report.rituals.map((ritual, index) => (
                <div
                  key={index}
                  className="flex items-stretch border border-gray-300 rounded-md p-3 bg-white transition-shadow duration-200"
                >
                  <div
                    className={`w-12 h-10 md:w-14 md:h-12 ${report.headerBg} rounded flex items-center justify-center font-bold text-gray-700 text-base flex-shrink-0 my-auto`}
                  >
                    {ritual.id}
                  </div>

                  <div className="flex-1 pl-5 text-xs md:text-sm text-gray-800 leading-relaxed flex items-center">
                    <p>
                      <span className="font-bold uppercase tracking-wide">
                        Trigger:
                      </span>{" "}
                      {ritual.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default PersonalAction;